
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { ApplicationForm } from "@/components/ApplicationForm";
import { ConsultationForm } from "@/components/ConsultationForm";
import { LanguageSelect } from "@/components/ui/select";

const translations = {
  en: {
    home: "Home",
    about: "About Us",
    services: "Services",
    destinations: "Study Destinations",
    blog: "Blog",
    testimonials: "Success Stories",
    apply: "Apply Now",
    contact: "Contact",
    consultation: "Free Consultation"
  },
  ar: {
    home: "الرئيسية",
    about: "من نحن",
    services: "خدماتنا",
    destinations: "وجهات الدراسة",
    blog: "المدونة",
    testimonials: "قصص النجاح",
    apply: "قدم الآن",
    contact: "اتصل بنا",
    consultation: "استشارة مجانية"
  },
  so: {
    home: "Bogga Hore",
    about: "Ku Saabsan",
    services: "Adeegyada",
    destinations: "Meelaha Waxbarashada",
    blog: "Blog",
    testimonials: "Sheekooyin Guul ah",
    apply: "Dalbo Hadda",
    contact: "La Xiriir",
    consultation: "La-talin Bilaash ah"
  }
};

function Navbar({ language, setLanguage }) {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMobileMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavLinkClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      const navbarHeight = 80; // Approximate navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: "home", label: translations[language].home },
    { id: "about", label: translations[language].about },
    { id: "services", label: translations[language].services },
    { id: "destinations", label: translations[language].destinations },
    { id: "blog", label: translations[language].blog },
    { id: "testimonials", label: translations[language].testimonials },
    { id: "contact", label: translations[language].contact }
  ];

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="logo-container">
            <a href="#home" onClick={(e) => handleNavLinkClick(e, "home")}>
              <img 
                src="https://storage.googleapis.com/hostinger-horizons-assets-prod/2035fa37-35aa-4837-b61a-e7175478d8be/674b8873a7b7a472e81bf1b0f5d0f13c.png" 
                alt="The Horn Educational Network Logo" 
                className="h-16 w-auto"
              />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <motion.a
                key={link.id}
                href={`#${link.id}`}
                className="nav-link"
                whileHover={{ y: -2 }}
                onClick={(e) => handleNavLinkClick(e, link.id)}
              >
                {link.label}
              </motion.a>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <LanguageSelect
              value={language}
              onChange={setLanguage}
            />

            <Button 
              variant="outline" 
              className="hidden md:inline-flex consultation-button"
              onClick={() => setShowConsultationForm(true)}
            >
              {translations[language].consultation}
            </Button>
            <Button 
              className="hidden md:inline-flex apply-button"
              onClick={() => setShowApplicationForm(true)}
            >
              {translations[language].apply}
            </Button>

            <Button 
              variant="ghost" 
              className="md:hidden"
              onClick={handleMobileMenuClick}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4"
            >
              <div className="flex flex-col space-y-4 py-4">
                {navLinks.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    className="nav-link text-lg"
                    onClick={(e) => handleNavLinkClick(e, link.id)}
                  >
                    {link.label}
                  </a>
                ))}
                <Button 
                  variant="outline" 
                  className="w-full consultation-button"
                  onClick={() => {
                    setShowConsultationForm(true);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {translations[language].consultation}
                </Button>
                <Button 
                  className="w-full apply-button"
                  onClick={() => {
                    setShowApplicationForm(true);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {translations[language].apply}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ApplicationForm 
        language={language}
        open={showApplicationForm}
        onOpenChange={setShowApplicationForm}
      />
      
      <ConsultationForm
        language={language}
        open={showConsultationForm}
        onOpenChange={setShowConsultationForm}
      />
    </nav>
  );
}

export default Navbar;
